import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { MENTORS } from '../data/mentors';

const { 
  FiTrendingUp, 
  FiActivity, 
  FiPieChart, 
  FiList, 
  FiSearch,
  FiTarget,
  FiXCircle,
  FiShield,
  FiBarChart2
} = FiIcons;

const GARDEN_HERO_IMAGE = "https://cdn.midjourney.com/763c9aab-d43a-4fc5-bb3e-b4f0887e2877/0_0.webp"; 

export default function DividendGarden() {
  const [activeTab, setActiveTab] = useState('tiers');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const marketScanData = [
    { category: 'Recovery Mode', pick: 'ARR', yield: '16.10%', status: 'STRONG BUY', note: 'Support bounce with green candles' },
    { category: 'Recovery Mode', pick: 'DX', yield: '14.99%', status: 'STRONG BUY', note: 'Technical reversal pattern' },
    { category: 'Conservative REIT', pick: 'AGNC', yield: '13.21%', status: 'STRONG BUY', note: 'Support level hold with momentum' },
    { category: 'BDC (Higher Risk)', pick: 'PSEC', yield: '15.93%', status: 'MODERATE BUY', note: 'Recovery from oversold territory' },
    { category: 'Ultra-High Yield', pick: 'EARN', yield: '22.10%', status: 'MODERATE BUY', note: 'Recent bottom formation' },
    { category: 'Downtrend', pick: 'HRZN', yield: '22.10%', status: 'STRONG SELL', note: 'Severe downtrend continuation' },
    { category: 'Downtrend', pick: 'PNNT', yield: '19.30%', status: 'STRONG SELL', note: 'No recovery signs' },
    { category: 'Downtrend', pick: 'IVR', yield: '17.03%', status: 'STRONG SELL', note: 'Persistent downtrend, lower lows' },
    { category: 'Downtrend', pick: 'TRIN', yield: '13.58%', status: 'STRONG SELL', note: 'Extended decline' },
    { category: 'Volatile', pick: 'ORC', yield: '19.46%', status: 'MIXED', note: 'Highly volatile, range-bound' },
    { category: 'Choppy', pick: 'PFLT', yield: '14.50%', status: 'MIXED', note: 'Choppy action, trend unclear' },
  ];

  const getStatusColor = (status) => {
    if (status.includes('STRONG BUY')) return 'text-green-700 bg-green-100 border-green-200';
    if (status.includes('MODERATE BUY') || status.includes('BUY')) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (status.includes('HOLD') || status.includes('MIXED')) return 'text-amber-600 bg-amber-50 border-amber-100';
    if (status.includes('STRONG SELL') || status.includes('SELL') || status.includes('AVOID')) return 'text-rose-600 bg-rose-50 border-rose-100';
    if (status.includes('WATCH')) return 'text-blue-600 bg-blue-50 border-blue-100';
    return 'text-stone-600 bg-stone-50 border-stone-100';
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-8 font-dm-sans text-stone-800">
      
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-3xl overflow-hidden shadow-2xl border border-stone-100 min-h-[300px] flex items-end"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${GARDEN_HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent"></div>
        </div>
        
        <div className="relative z-10 p-8 w-full">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-indigo-500/20 backdrop-blur-md rounded-lg border border-indigo-400/30">
              <SafeIcon icon={FiBarChart2} className="w-6 h-6 text-indigo-300" />
            </div>
            <span className="text-sm font-bold tracking-widest text-indigo-200 uppercase drop-shadow-md">March 30, 2026 Analysis</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            Dividend Garden
          </h1>
          <p className="text-lg text-stone-200 max-w-2xl drop-shadow-md">
            Cultivate your passive income with our comprehensive market scan. 
            Based on the top 15 highest-yielding monthly dividend stocks using <span className="text-indigo-300 font-bold">Sleepy Charging Rhino</span> indicators.
          </p>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 md:gap-4 border-b border-stone-200 pb-1">
        {[
          { id: 'tiers', label: 'Top Stocks', icon: FiList },
          { id: 'analysis', label: 'Market Scan', icon: FiSearch },
          { id: 'portfolio', label: 'Portfolio Plan', icon: FiPieChart },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-t-xl transition-all relative ${
              activeTab === tab.id 
                ? 'text-indigo-700 bg-white border-x border-t border-stone-200 shadow-sm translate-y-[1px] z-10' 
                : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
            }`}
          >
            <SafeIcon icon={tab.icon} className={`w-4 h-4 ${activeTab === tab.id ? 'text-indigo-600' : ''}`} />
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute top-0 left-0 w-full h-[3px] bg-indigo-600 rounded-t-xl"></div>
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        
        {/* TAB 1: STOCK TIERS */}
        {activeTab === 'tiers' && (
          <motion.div
            key="tiers"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-12"
          >
            {/* TIER 1 - BUTLER */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl border border-stone-200 shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                <div className="lg:col-span-3 bg-stone-100 relative min-h-[200px] lg:min-h-full">
                   <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${MENTORS.butler.image})` }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                    <h3 className="text-white font-bold text-xl">Core Portfolio</h3>
                    <p className="text-stone-300 text-sm">Strong Buy Recovery Signals</p>
                  </div>
                </div>
                <div className="lg:col-span-9 p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 bg-green-100 rounded-full">
                      <SafeIcon icon={FiShield} className="text-green-600 w-5 h-5" />
                    </div>
                    <p className="text-stone-500 text-sm font-medium">Clear BUY signals confirmed by Sleepy Charging Rhino green checkmarks. Strong technical recovery patterns.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {[
                      { ticker: 'ARR', name: 'ARMOUR Residential', yield: '16.10%', status: 'STRONG BUY', note: 'Target $17.50+' },
                      { ticker: 'DX', name: 'Dynex Capital', yield: '14.99%', status: 'STRONG BUY', note: 'Target $13.50+' },
                      { ticker: 'AGNC', name: 'AGNC Investment', yield: '13.21%', status: 'STRONG BUY', note: 'Target $10.75+' },
                      { ticker: 'PSEC', name: 'Prospect Capital', yield: '15.93%', status: 'MODERATE BUY', note: 'Target $2.95+' },
                    ].map((stock) => (
                      <div key={stock.ticker} className="p-4 rounded-xl border border-stone-100 bg-stone-50/50 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-bold text-lg text-stone-900 block">{stock.ticker}</span>
                            <span className="text-xs text-stone-500">{stock.name}</span>
                          </div>
                          <span className={`text-[10px] px-2 py-1 rounded-full font-bold border ${getStatusColor(stock.status)}`}>
                            {stock.status}
                          </span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-stone-400 text-xs block">Yield</span>
                            <span className="font-bold text-stone-700">{stock.yield}</span>
                          </div>
                          <div className="text-right">
                             <span className="text-stone-400 text-xs block">Insight</span>
                             <span className="text-stone-600 text-xs leading-tight">{stock.note}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* TIER 2 - LEO */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl border border-stone-200 shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                <div className="lg:col-span-3 bg-stone-100 relative min-h-[200px] lg:min-h-full">
                   <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${MENTORS.leo.image})` }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                    <h3 className="text-white font-bold text-xl">High Yield Edge</h3>
                    <p className="text-stone-300 text-sm">Speculative Income Plays</p>
                  </div>
                </div>
                <div className="lg:col-span-9 p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <SafeIcon icon={FiTrendingUp} className="text-amber-600 w-5 h-5" />
                    </div>
                    <p className="text-stone-500 text-sm font-medium">Ultra-high yields requiring smaller position sizing and strict risk management.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {[
                      { ticker: 'EARN', name: 'Ellington Credit Co.', yield: '22.10%', status: 'MODERATE BUY', note: 'Recent bottom formed' },
                      { ticker: 'OXSQ', name: 'Oxford Square Cap', yield: '23.50%', status: 'WATCH', note: 'Extreme volatility' },
                    ].map((stock) => (
                      <div key={stock.ticker} className="p-4 rounded-xl border border-stone-100 bg-stone-50/50 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-bold text-lg text-stone-900 block">{stock.ticker}</span>
                            <span className="text-xs text-stone-500">{stock.name}</span>
                          </div>
                          <span className={`text-[10px] px-2 py-1 rounded-full font-bold border ${getStatusColor(stock.status)}`}>
                            {stock.status}
                          </span>
                        </div>
                         <div className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-stone-400 text-xs block">Yield</span>
                            <span className="font-bold text-stone-700">{stock.yield}</span>
                          </div>
                          <div className="text-right">
                             <span className="text-stone-400 text-xs block">Insight</span>
                             <span className="text-stone-600 text-xs leading-tight">{stock.note}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* TIER 3 - AVOID */}
            <motion.div variants={itemVariants} className="bg-rose-50 rounded-3xl border border-rose-100 p-8 shadow-sm">
               <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-rose-200 rounded-full">
                  <SafeIcon icon={FiXCircle} className="text-rose-700 w-6 h-6" />
                </div>
                <h3 className="font-bold text-xl text-rose-900">Strong Sell / Avoid</h3>
              </div>
              <p className="text-rose-800 mb-6 max-w-2xl">
                Multiple Sleepy Charging Rhino sell signals (Red X), extended downtrends, or highly volatile action. High risk of capital loss despite massive yields.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { t: 'HRZN', y: '22.1%', r: 'Downtrend' }, 
                  { t: 'PNNT', y: '19.3%', r: 'Downtrend' }, 
                  { t: 'ORC', y: '19.4%', r: 'Volatile' }, 
                  { t: 'IVR', y: '17.0%', r: 'Lower Lows' }, 
                  { t: 'TRIN', y: '13.5%', r: 'Downtrend' },
                  { t: 'PFLT', y: '14.5%', r: 'Choppy' }
                ].map(item => (
                  <span key={item.t} className="bg-white border border-rose-200 text-rose-700 px-4 py-2 rounded-lg font-bold shadow-sm flex items-center gap-2">
                    {item.t} <span className="text-xs font-normal text-rose-500">({item.y})</span>
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* TAB 2: MARKET SCAN ANALYSIS */}
        {activeTab === 'analysis' && (
          <motion.div
            key="analysis"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Rhino Analysis Card */}
              <motion.div variants={itemVariants} className="lg:col-span-1 bg-stone-900 rounded-3xl overflow-hidden shadow-xl text-white relative">
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: `url(${MENTORS.rhino.image})` }}
                  ></div>
                  <div className="relative z-10 p-8 h-full flex flex-col">
                    <div className="mb-auto">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/30 border border-indigo-400/30 text-indigo-200 text-xs font-bold mb-4">
                        <SafeIcon icon={FiActivity} className="w-3 h-3" />
                        SLEEPY CHARGING RHINO SCANNER
                      </div>
                      <h3 className="text-2xl font-bold mb-4">Signal Interpretation</h3>
                      <div className="space-y-4 text-sm text-stone-300">
                        <p><strong className="text-green-400">Green Checkmark:</strong> Buy confirmation + Support Bounce.</p>
                        <p><strong className="text-emerald-400">Moderate Buy:</strong> Reversal from oversold, wait for volume.</p>
                        <p><strong className="text-rose-400">Red X (Sell):</strong> Multiple sell signals + extended decline.</p>
                      </div>
                    </div>
                  </div>
              </motion.div>

              {/* Table */}
              <motion.div variants={itemVariants} className="lg:col-span-2 bg-white p-8 rounded-3xl border border-stone-200 shadow-lg">
                <h3 className="text-xl font-bold text-stone-900 mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiSearch} className="text-indigo-600" /> Market Scan Summary
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-stone-50 text-stone-500 uppercase tracking-wider">
                        <th className="p-4 rounded-l-xl">Category</th>
                        <th className="p-4">Best Pick</th>
                        <th className="p-4">Yield</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 rounded-r-xl">Insight</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {marketScanData.map((item, idx) => (
                        <tr key={idx} className="hover:bg-stone-50 transition-colors">
                          <td className="p-4 font-bold text-stone-700">{item.category}</td>
                          <td className="p-4 font-mono text-stone-900 font-bold">{item.pick}</td>
                          <td className="p-4 text-stone-600">{item.yield}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="p-4 text-stone-500 italic">{item.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* TAB 3: PORTFOLIO PLAN */}
        {activeTab === 'portfolio' && (
          <motion.div
            key="portfolio"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-6"
          >
             <motion.div variants={itemVariants} className="bg-white p-8 rounded-3xl border border-stone-200 shadow-lg">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <SafeIcon icon={FiPieChart} className="text-indigo-600 w-6 h-6" />
                    </div>
                    Recommended Allocation
                  </h3>
                  <div className="px-4 py-2 bg-green-100 text-green-800 rounded-xl font-bold text-sm">
                    Est. Annual Return: 12-15%
                  </div>
                </div>
                
                <div className="space-y-8">
                  {/* Core Portfolio */}
                  <div>
                    <div className="flex justify-between items-end mb-3">
                      <div>
                        <h4 className="font-bold text-lg text-stone-800">CORE PORTFOLIO</h4>
                        <p className="text-stone-500 text-sm">Most stable recovery picks with strong technicals</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-indigo-600">70%</span>
                        <span className="text-stone-400 text-sm block">Allocation</span>
                      </div>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-3 mb-4 overflow-hidden">
                      <div className="bg-indigo-500 h-full rounded-full shadow-sm" style={{ width: '70%' }}></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center hover:bg-indigo-100 transition-colors">
                        <span className="block font-bold text-indigo-900 text-lg">ARR</span>
                        <span className="text-indigo-600 font-medium text-sm">35% Weight</span>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center hover:bg-indigo-100 transition-colors">
                        <span className="block font-bold text-indigo-900 text-lg">DX</span>
                        <span className="text-indigo-600 font-medium text-sm">25% Weight</span>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center hover:bg-indigo-100 transition-colors">
                        <span className="block font-bold text-indigo-900 text-lg">AGNC</span>
                        <span className="text-indigo-600 font-medium text-sm">20% Weight</span>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center hover:bg-indigo-100 transition-colors">
                        <span className="block font-bold text-indigo-900 text-lg">PSEC</span>
                        <span className="text-indigo-600 font-medium text-sm">10% Weight</span>
                      </div>
                    </div>
                  </div>

                  {/* Speculative */}
                  <div>
                    <div className="flex justify-between items-end mb-3">
                       <div>
                        <h4 className="font-bold text-lg text-stone-800">SPECULATIVE / HIGH-YIELD</h4>
                        <p className="text-stone-500 text-sm">Ultra-high yields, manage size carefully</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-emerald-600">30%</span>
                        <span className="text-stone-400 text-sm block">Allocation</span>
                      </div>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-3 mb-4 overflow-hidden">
                      <div className="bg-emerald-500 h-full rounded-full shadow-sm" style={{ width: '30%' }}></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-center hover:bg-emerald-100 transition-colors">
                        <span className="block font-bold text-emerald-900 text-lg">EARN</span>
                        <span className="text-emerald-600 font-medium text-sm">15% Weight</span>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-center hover:bg-emerald-100 transition-colors">
                        <span className="block font-bold text-emerald-900 text-lg">OXSQ</span>
                        <span className="text-emerald-600 font-medium text-sm">10% Weight</span>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-center hover:bg-emerald-100 transition-colors">
                        <span className="block font-bold text-emerald-900 text-lg">CASH</span>
                        <span className="text-emerald-600 font-medium text-sm">5% Reserve</span>
                      </div>
                    </div>
                  </div>
                </div>

                 <div className="mt-8 pt-8 border-t border-stone-100 flex flex-col md:flex-row justify-between items-center text-sm bg-stone-50 p-6 rounded-2xl">
                    <span className="font-bold text-stone-600 mb-2 md:mb-0">Income Projection:</span>
                    <span className="font-bold text-lg text-stone-700">~$150/month ($1,800/yr) on $30k investment.</span>
                 </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white p-8 rounded-3xl border border-stone-200 shadow-lg">
              <h3 className="text-xl font-bold text-stone-900 mb-6 flex items-center gap-2">
                <SafeIcon icon={FiTarget} className="text-indigo-600" /> Sleepy Charging Rhino Rules System
              </h3>
              <ul className="space-y-4 text-sm text-stone-600">
                <li className="flex items-start gap-4 p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                  <span className="bg-indigo-200 text-indigo-800 font-bold w-6 h-6 flex items-center justify-center rounded-full text-xs mt-0.5 shadow-sm">1</span>
                  <span><strong>Entry Rules:</strong> Wait for Green checkmark confirmation + Support level bounce. Target entry zone 1-2% below green signal.</span>
                </li>
                <li className="flex items-start gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <span className="bg-emerald-200 text-emerald-800 font-bold w-6 h-6 flex items-center justify-center rounded-full text-xs mt-0.5 shadow-sm">2</span>
                  <span><strong>Exit Rules:</strong> Red X appears = Exit 50%. Multiple red signals = Exit 75%. Support break = Exit remaining.</span>
                </li>
                <li className="flex items-start gap-4 p-4 rounded-xl bg-amber-50 border border-amber-100">
                  <span className="bg-amber-200 text-amber-800 font-bold w-6 h-6 flex items-center justify-center rounded-full text-xs mt-0.5 shadow-sm">3</span>
                  <span><strong>Stop Loss:</strong> 5-10% below entry for safe positions (ARR, AGNC), 15%+ for high-yield plays (EARN).</span>
                </li>
                <li className="flex items-start gap-4 p-4 rounded-xl bg-rose-50 border border-rose-100">
                  <span className="bg-rose-200 text-rose-800 font-bold w-6 h-6 flex items-center justify-center rounded-full text-xs mt-0.5 shadow-sm">4</span>
                  <span><strong>Avoid High Yield Traps:</strong> Do not chase HRZN/TRIN. Their 13-22% yields are warning signals of severe downtrends.</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>

    </div>
  );
}